// Adaptive Module 3: Order Entry (Proprioceptive Memory)
// Uses MODULE_3_INSTANCES for difficulty-based button sequence tasks

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'antd';
import { FaChevronRight } from 'react-icons/fa';
import { useRecoilState } from 'recoil';
import { sessionTimingAtom } from '../../../../state/assessment';
import AssessmentWrapper from '../../AssessmentWrapper';
import { MODULE_3_INSTANCES } from '../../../../data/moduleInstances';
import Close from '../../../../resources/imgs/modal/close.svg';
import HelpBtn from '../../../../resources/imgs/assessment/helpBtn.svg';

// Audio file paths (using public directory)
// Use PUBLIC_URL for correct path in production
const basePath = process.env.PUBLIC_URL || '';
const BUTTON_SOUNDS = {
  1: `${basePath}/sounds/module3/button1.wav`,
  2: `${basePath}/sounds/module3/button2.wav`,
  3: `${basePath}/sounds/module3/button3.wav`,
  4: `${basePath}/sounds/module3/button4.wav`
};

const OrderEntryContent = ({ instance, onSubmit, instanceNumber, moduleId }) => {
  const [processIndex, setProcessIndex] = useState(0);
  const [counter, setCounter] = useState(0);
  const [isShowing, setIsShowing] = useState(0);
  const [res, setRes] = useState([0, 0, 0, 0]);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef(null);
  const [userSequence, setUserSequence] = useState([]);
  const [hasStarted, setHasStarted] = useState(false); // Track if user has started the sequence
  const audioRefs = useRef({});
  const [sessionTiming, setSessionTiming] = useRecoilState(sessionTimingAtom);
  const [instanceStartTime, setInstanceStartTime] = useState(null);
  
  // Initialize audio objects
  useEffect(() => {
    // Create audio objects for each button
    Object.keys(BUTTON_SOUNDS).forEach(buttonNum => {
      const audio = new Audio(BUTTON_SOUNDS[buttonNum]);
      audio.preload = 'auto';
      audio.volume = 0.7; // Set volume to 70%
      audioRefs.current[buttonNum] = audio;
      
      console.log(`🔊 Loaded audio for button ${buttonNum}: ${BUTTON_SOUNDS[buttonNum]}`);
    });
    
    // Cleanup function
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    };
  }, []);
  
  // Function to play button sound
  const playSound = useCallback((buttonIndex) => {
    console.log(`🔊 Attempting to play sound for button ${buttonIndex}`);
    
    if (audioRefs.current[buttonIndex]) {
      const audio = audioRefs.current[buttonIndex];
      audio.currentTime = 0; // Reset to start
      
      audio.play()
        .then(() => {
          console.log(`✅ Successfully played sound for button ${buttonIndex}`);
        })
        .catch(error => {
          console.error(`❌ Failed to play sound for button ${buttonIndex}:`, error);
          
          // Try to handle user interaction requirement for audio
          if (error.name === 'NotAllowedError') {
            console.log('🔊 Audio blocked - user interaction required first');
          }
        });
    } else {
      console.error(`❌ No audio found for button ${buttonIndex}`);
    }
  }, []);

  // Initialize modal and start sequence when instance changes
  useEffect(() => {
    if (!instance) return;

    console.log('🔄 Module 3 useEffect triggered - instance:', instance?.instanceId);

    // Reset state for new instance
    setProcessIndex(0);
    setCounter(0);
    setIsShowing(0);
    setRes([0, 0, 0, 0]);
    setUserSequence([]);
    setHasStarted(false);

    // Initialize session start time if not already set
    const now = Date.now();
    if (!sessionTiming.sessionStartTime) {
      setSessionTiming(prev => ({
        ...prev,
        sessionStartTime: now
      }));
    }

    // Record instance start time
    setInstanceStartTime(now);
    console.log(`⏱️ Module 3 Instance ${instanceNumber} started at ${now - (sessionTiming.sessionStartTime || now)}ms from session start`);

    if (instance.showSequence) {
      // For instances that show sequence, don't start automatically
      setOpen(true);
      // User will click start button
    } else {
      // For memory-based instances, user needs to manually start
      setIsShowing(1);
    }
  }, [instance?.instanceId, sessionTiming.sessionStartTime, instanceNumber]); // Only trigger on instance ID change, not entire object

  const startSequence = useCallback(() => {
    if (!instance || !instance.mealOrder) return;
    
    console.log('🚀 Starting sequence for meals:', instance.mealOrder);
    
    let currentProcessIndex = 0;
    let currentCounter = 0;
    
    setProcessIndex(0);
    setCounter(0);
    setHasStarted(true); // Mark that user has started
    
    // Start showing the sequence
    intervalRef.current = setInterval(() => {
      currentCounter++;
      setCounter(currentCounter);
      
      console.log(`⏱️ Counter: ${currentCounter}, ProcessIndex: ${currentProcessIndex}`);
      
      // Play sound for buttons 1-4 during the sequence display
      if (currentCounter >= 1 && currentCounter <= 4) {
        const currentButtonSequence = instance.buttonSequence[currentProcessIndex];
        const buttonIndex = currentButtonSequence[currentCounter - 1];
        console.log(`🎵 Playing sound for meal ${instance.mealOrder[currentProcessIndex]}, step ${currentCounter}, button ${buttonIndex}`);
        
        if (buttonIndex) {
          // Add a small delay to sync with the visual highlight
          setTimeout(() => playSound(buttonIndex), 100);
        }
      }
      
      if (currentCounter >= 7) {
        currentProcessIndex++;
        currentCounter = 0;
        setProcessIndex(currentProcessIndex);
        setCounter(0);
        
        if (currentProcessIndex >= instance.mealOrder.length) {
          // Sequence complete, allow user input
          console.log('✅ Sequence display complete, allowing user input');
          setIsShowing(1);
          clearInterval(intervalRef.current);
        }
      }
    }, 500);
  }, [instance, playSound]);

  const handleButtonPress = useCallback((buttonIndex) => {
    if (!isShowing || !instance) return;
    
    // Play sound when user presses button
    playSound(buttonIndex);
    
    setRes(prevRes => {
      const newRes = [...prevRes];
      newRes[buttonIndex - 1] = 1;
      return newRes;
    });
    
    setUserSequence(prev => {
      const newSequence = [...prev, buttonIndex];
      
      // Check if sequence is complete
      const totalExpectedButtons = instance.buttonSequence.flat().length;
      if (newSequence.length >= totalExpectedButtons) {
        // Complete sequence entered, check correctness
        setTimeout(() => {
          checkSolution(newSequence);
        }, 500);
      }
      
      return newSequence;
    });
  }, [isShowing, instance, playSound, checkSolution]); // Removed res and userSequence from deps

  const checkSolution = useCallback((fullUserSequence) => {
    if (!instance) return;

    const expectedSequence = instance.buttonSequence.flat();
    const isCorrect = JSON.stringify(expectedSequence) === JSON.stringify(fullUserSequence);

    console.log('Expected sequence:', expectedSequence);
    console.log('User sequence:', fullUserSequence);
    console.log('Is correct:', isCorrect);

    // Record trial timing data
    const now = Date.now();
    const sessionStart = sessionTiming.sessionStartTime || now;
    const trialStart = instanceStartTime || now;

    // Determine error type
    let errorType = 'none';
    if (!isCorrect) {
      // Check if it's a sequence error (wrong order) vs accuracy error (wrong buttons)
      const correctButtons = new Set(expectedSequence);
      const userButtons = new Set(fullUserSequence);
      const hasWrongButtons = [...userButtons].some(btn => !correctButtons.has(btn));
      errorType = hasWrongButtons ? 'accuracy' : 'sequence';
    }

    const trialData = {
      module_id: '3',
      instance_id: instance?.instanceId?.toString() || instanceNumber?.toString(),
      timestamp_start_ms: trialStart - sessionStart,
      timestamp_response_ms: now - sessionStart,
      response_latency_ms: now - trialStart,
      is_correct: isCorrect,
      error_type: errorType,
      difficulty_level: instance.difficulty,
      trial_number: instanceNumber
    };

    console.log('⏱️ Module 3 trial data:', trialData);

    // Add to session timing atom
    setSessionTiming(prev => ({
      ...prev,
      trialData: [...prev.trialData, trialData]
    }));

    onSubmit(isCorrect, {
      expectedSequence,
      userSequence: fullUserSequence,
      mealOrder: instance.mealOrder,
      difficulty: instance.difficulty,
      trialData
    });
  }, [instance, onSubmit, sessionTiming, instanceStartTime, instanceNumber, setSessionTiming]);

  const resetSequence = useCallback(() => {
    setRes([0, 0, 0, 0]);
    setUserSequence([]);
    setHasStarted(false);
  }, []);

  const showModal = () => setOpen(true);
  const hideModal = () => setOpen(false);

  if (!instance) {
    return <div>Loading order entry...</div>;
  }

  return (
    <div className="module1-main">
      <div className="module1-wrap">
        <div className="module1-content-wrap">
          <div className="module1-logo">Module 3 - {instance.title}</div>
          <div className="module1-body">
            <div className="module1-title">
              Order Entry (Difficulty: {instance.difficulty}/10) - Instance {instanceNumber}/5
            </div>
            <div className="module1-content1">
              {instance.task}
            </div>
          </div>
        </div>
      </div>

      <div className="module1-dishes">
        <div className="module3-sequence-display">
          <div>Meals to enter: {instance.mealOrder.join(' → ')}</div>
          {processIndex < instance.mealOrder.length && !isShowing && (
            <div>Showing sequence for: {instance.mealOrder[processIndex]}</div>
          )}
        </div>
      </div>

      <div className="module1-content">
        <div className="module3-buttons">
          {[1, 2, 3, 4].map((buttonNum) => (
            <div 
              key={buttonNum}
              className={`module4-dish module4-dish${buttonNum} hover-none`} 
              onClick={() => handleButtonPress(buttonNum)}
              style={{ 
                backgroundColor: (
                  (counter > buttonNum && processIndex < instance.mealOrder.length) || 
                  res[buttonNum - 1]
                ) ? getButtonColor(buttonNum) : "white" 
              }}
            />
          ))}
        </div>
      </div>

      <div className='next-btn-group'>
        <div className='help-btn-group'>
          <div className='help-btn-effect1'></div>
          <div className='help-btn-effect2'></div>
          <img src={HelpBtn} onClick={showModal} alt="Help" />
          <div className='help-content'>Do you need help?</div>
        </div>

        <div className="assessment-reset-btn" onClick={resetSequence}>
          Reset
        </div>

        {instance.showSequence && !hasStarted && (
          <div className="assessment-next-btn" onClick={startSequence}>
            Start
          </div>
        )}
      </div>

      <Modal
        open={open}
        onOk={showModal}
        onCancel={hideModal}
        closeIcon={<img src={Close} alt="Close" />}
        footer={null}
      >
        <div className="direction-modal-container">
          <div className='direction-modal-title'>Directions</div>
          <div className='direction-modal-content'>
            <div className="assessment1-content direction-content">
              Watch the pattern closely so you can replicate it. {!hasStarted && "Press start when you're ready to observe the pattern."}
            </div>
            <div className="assessment1-content direction-content">
              Each meal has its own unique button pattern that you need to memorize and replicate.
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Helper function for button colors
const getButtonColor = (buttonNum) => {
  const colors = {
    1: "#155226",
    2: "red", 
    3: "#8C7421",
    4: "blue"
  };
  return colors[buttonNum] || "white";
};

// Adaptive Module 3 Component
const AdaptiveModule3 = (props) => (
  <AssessmentWrapper 
    moduleType="3" 
    isAdaptive={true}
    onModuleComplete={props.onModuleComplete}
    renderAdaptiveContent={(contentProps) => <OrderEntryContent {...contentProps} />}
  />
);

export default AdaptiveModule3;